import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_HOST } from '../../../environments/environment';

/**
 * KSeF Monitoring API Service
 * 
 * Endpoints dla admin panelu KSeF
 * Statystyki, logi, pending batches, sync status, XML viewer
 */
@Injectable({
  providedIn: 'root'
})
export class KsefMonitoringApiService {

  private readonly baseUrl = API_HOST + 'admin/ksef';

  constructor(private http: HttpClient) { }

  // ==========================================
  // STATYSTYKI
  // ==========================================

  /**
   * GET /admin/ksef/stats
   * Pobierz statystyki wysyłek batch (today, 7 days, 30 days, per hour)
   */
  getBatchStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/stats`);
  }

  // ==========================================
  // PENDING BATCHES
  // ==========================================

  /**
   * GET /admin/ksef/pending-batches
   * Lista userów z pending documents
   */
  getPendingBatches(): Observable<any> {
    return this.http.get(`${this.baseUrl}/pending-batches`);
  }

  // ==========================================
  // SYNC STATUS
  // ==========================================

  /**
   * GET /admin/ksef/sync-status
   * Status synchronizacji dla wszystkich userów
   */
  getSyncStatus(): Observable<any> {
    return this.http.get(`${this.baseUrl}/sync-status`);
  }

  // ==========================================
  // BATCH LOGS
  // ==========================================

  /**
   * GET /admin/ksef/batch-logs
   * Wszystkie batch logs (z filtrowaniem i paginacją server-side)
   */
  getBatchLogs(filters?: {
    userId?: string;
    logType?: string;
    logTypes?: string[];  // Multi-select log types
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;      // Wyszukiwanie po email, dokumentNumber, errorMessage
    page?: number;        // Numer strony (1-based)
    pageSize?: number;    // Rozmiar strony (domyślnie 50)
    limit?: number;       // Legacy - zachowane dla kompatybilności
  }): Observable<any> {
    // Buduj parametry - HttpClient automatycznie serializuje tablice
    const params: any = {};
    
    if (filters?.userId) params.userId = filters.userId;
    if (filters?.logType) params.logType = filters.logType;
    if (filters?.logTypes && filters.logTypes.length > 0) {
      params['logTypes[]'] = filters.logTypes;  // Express parsuje jako array
    }
    if (filters?.status) params.status = filters.status;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.search) params.search = filters.search;
    if (filters?.page) params.page = filters.page;
    if (filters?.pageSize) params.pageSize = filters.pageSize;
    if (filters?.limit) params.limit = filters.limit;
    
    return this.http.get(`${this.baseUrl}/batch-logs`, { params });
  }

  /**
   * GET /admin/ksef/batch-logs/session/:sessionRef
   * Logi dla konkretnej sesji batch
   */
  getSessionLogs(sessionRef: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/batch-logs/session/${sessionRef}`);
  }

  /**
   * GET /admin/ksef/batch-logs/cron
   * Logi CRON (ostatnie 20)
   */
  getCronLogs(limit: number = 20): Observable<any> {
    return this.http.get(`${this.baseUrl}/batch-logs/cron`, { params: { limit } });
  }

  // ==========================================
  // XML VIEWER
  // ==========================================

  /**
   * GET /admin/ksef/documents/:documentId/xml
   * Pobierz XML dla dokumentu
   * 🔑 KLUCZOWY ENDPOINT DLA XML VIEWER
   */
  getDocumentXml(documentId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/documents/${documentId}/xml`);
  }

  /**
   * GET /admin/ksef/invoices/:ksefNumber/xml
   * Pobierz XML po numerze KSeF
   */
  getXmlByKsefNumber(ksefNumber: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/invoices/${ksefNumber}/xml`);
  }

  /**
   * GET /admin/ksef/documents/:documentId/xml/download
   * Download XML jako plik
   * 
   * UWAGA: Użyj window.open() lub anchor download
   */
  downloadXmlUrl(documentId: string): string {
    return `${this.baseUrl}/documents/${documentId}/xml/download`;
  }

  // ==========================================
  // MANUAL SYNC TRIGGER
  // ==========================================

  /**
   * POST /admin/ksef/trigger-sync
   * Ręczne uruchomienie synchronizacji faktur z KSeF
   */
  triggerManualSync(): Observable<any> {
    return this.http.post(`${this.baseUrl}/trigger-sync`, {});
  }

  /**
   * POST /admin/ksef/diagnose-sync
   * Diagnostyka synchronizacji dla konkretnego usera (po NIP)
   */
  diagnoseSync(nip: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/diagnose-sync`, { nip });
  }

  // ==========================================
  // WERYFIKACJA NIP
  // ==========================================

  /**
   * GET /admin/ksef/invalid-nips
   * Lista userów z nieprawidłowym NIP
   */
  getInvalidNips(): Observable<any> {
    return this.http.get(`${this.baseUrl}/invalid-nips`);
  }

  /**
   * POST /admin/ksef/clean-nip
   * Czyści NIP użytkownika (usuwa znaki niebędące cyframi)
   */
  cleanNip(userId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/clean-nip`, { userId });
  }
}

